import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import "../../assets/styles/ClientShared.css";

const CarePlanRequestDetail = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState("");
  const [projectData, setProjectData] = useState(null);
  const [requestIndex, setRequestIndex] = useState(-1);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        const snap = await getDoc(doc(db, "projects", userData.projectId));
        if (!snap.exists()) throw new Error("Project not found.");

        const data = snap.data();
        const decodedRequestId = decodeURIComponent(requestId);
        const index = (data.carePlanRequests || []).findIndex((item, i) =>
          item.id === decodedRequestId || item.submittedAt === decodedRequestId || String(i) === decodedRequestId
        );

        setProjectId(userData.projectId);
        setProjectData(data);
        setRequestIndex(index);
      } catch (error) {
        console.error("Error loading request:", error);
        setMessage("Unable to load this request.");
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [requestId]);

  const request = projectData?.carePlanRequests?.[requestIndex];
  const messages = request?.messages?.length
    ? request.messages
    : request
      ? [{
          id: "initial-message",
          author: "client",
          body: request.details,
          createdAt: request.submittedAt,
        }]
      : [];

  const handleSendReply = async () => {
    if (!reply.trim() || requestIndex < 0) return;

    try {
      setSaving(true);
      const nextMessage = {
        id: `msg-${Date.now()}`,
        author: "client",
        body: reply.trim(),
        createdAt: new Date().toISOString(),
      };
      const updatedRequests = projectData.carePlanRequests.map((item, index) =>
        index === requestIndex
          ? { ...item, messages: [...messages, nextMessage] }
          : item
      );

      await updateDoc(doc(db, "projects", projectId), {
        carePlanRequests: updatedRequests,
        latestCarePlanRequest: updatedRequests[updatedRequests.length - 1] || null,
      });

      setProjectData(prev => ({ ...prev, carePlanRequests: updatedRequests }));
      setReply("");
      setMessage("Reply sent.");
    } catch (error) {
      console.error("Error sending reply:", error);
      setMessage("Unable to send reply right now.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="cp-loading"><p>Loading request...</p></div>;

  if (!request) {
    return (
      <div className="cp-page cp-page--narrow">
        <div className="cp-page-header">
          <p className="cp-eyebrow">Client Portal</p>
          <h1 className="cp-title">Request Not Found</h1>
        </div>
        <button className="cp-btn-secondary" onClick={() => navigate("/client/care-plan-request")}>Back to Requests</button>
      </div>
    );
  }

  return (
    <div className="cp-page cp-page--narrow">
      <div className="cp-page-header">
        <button className="cp-doc-link" onClick={() => navigate("/client/care-plan-request")} style={{ marginBottom: "14px" }}>
          &larr; Back to requests
        </button>
        <p className="cp-eyebrow">Care Plan Request</p>
        <h1 className="cp-title">{request.requestType || "Website Update Request"}</h1>
        <p className="cp-subtitle">{request.pageOrFeature || "No page or feature specified"}</p>
      </div>

      {message && <p className="cp-message" style={{ marginBottom: "20px" }}>{message}</p>}

      <div className="cp-section" style={{ marginBottom: "20px" }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Request Details</h2>
          <span className={`cp-badge ${request.status === "done" ? "cp-badge--active" : "cp-badge--pending"}`}>{request.status || "new"}</span>
        </div>
        <div className="cp-section-body">
          <ul className="cp-list">
            <li className="cp-list-item"><strong>Priority:</strong> {request.priority || "Normal"}</li>
            <li className="cp-list-item"><strong>Preferred deadline:</strong> {request.deadline || "Not provided"}</li>
            <li className="cp-list-item"><strong>Submitted:</strong> {request.submittedAt ? new Date(request.submittedAt).toLocaleString() : "Not available"}</li>
          </ul>
          <p style={{ fontFamily: "var(--font-body)", fontSize: "0.9375rem", color: "var(--text-secondary)", lineHeight: 1.7, margin: "16px 0 0" }}>
            {request.details}
          </p>
        </div>
      </div>

      <div className="cp-section">
        <div className="cp-section-header">
          <h2 className="cp-section-title">Conversation</h2>
        </div>
        <div className="cp-section-body">
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
            {messages.map((item) => (
              <div key={item.id || item.createdAt} style={{
                alignSelf: item.author === "client" ? "flex-end" : "flex-start",
                maxWidth: "82%",
                background: item.author === "client" ? "rgba(245, 78, 0, 0.08)" : "var(--surface-200)",
                color: "var(--text-primary)",
                border: item.author === "client" ? "1px solid rgba(245, 78, 0, 0.22)" : "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                padding: "12px 14px",
              }}>
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.75, margin: "0 0 6px" }}>
                  {item.author === "admin" ? "Next Level Websites" : "You"} {item.createdAt ? `· ${new Date(item.createdAt).toLocaleString()}` : ""}
                </p>
                <p style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem", lineHeight: 1.6, margin: 0 }}>{item.body}</p>
              </div>
            ))}
          </div>

          <div className="cp-form-group">
            <label>Reply</label>
            <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Ask a question, confirm a change, or add more details." />
          </div>
          <div className="cp-btn-row">
            <button className="cp-btn-primary" onClick={handleSendReply} disabled={saving || !reply.trim()}>
              {saving ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarePlanRequestDetail;
